/**
 * One search hit, rendered as the thing it is (§9.1).
 *
 * Three kinds, three rows, because the index gives each of them different
 * evidence: a document matched on its text and comes back with a fragment of
 * it, a file matched on its filename, uploader, or type, and a link matched on
 * its title or its URL. A single row shape would have to render the union of
 * those fields, which means rendering blanks for two thirds of every result —
 * and it would quietly stop showing *why* a row is here, which is the only
 * thing a search result is for.
 *
 * Nothing in this file drops a hit. §9.2 puts the ACL inside the statement so
 * that "post-filtering leaks the existence of documents through result counts
 * and pagination gaps"; a component that skipped a row it did not know how to
 * draw would reintroduce the gap on the client side, where the count beside it
 * came from the server. The discriminated union is exhaustive for that reason.
 */

import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import type { DocumentHit, FileHit, LinkHit, SearchHit } from '../../api/types.ts';
import { formatRelative } from '../../components/format.ts';
import { documentLinkProps, nodeLinkProps } from '../../components/tree/links.ts';
import {
  DocumentStatusBadge,
  DocumentTypeBadge,
  ExternalLinkMarker,
  NodeTypeBadge,
  ReviewBadge,
  WorkingDraftBadge,
} from '../../components/tree/markers.tsx';
import { Mono } from '../../components/ui/layout.tsx';
import { contentTypeLabel, formatBytes } from './format.ts';

/** The space a hit sits in, resolved by the page from the space list it already has. */
export type SpaceNames = ReadonlyMap<string, string>;

interface RowProps {
  spaceName: string | undefined;
}

function HitShell({
  title,
  badges,
  children,
  meta,
}: {
  title: ReactNode;
  badges: ReactNode;
  children?: ReactNode;
  meta: ReactNode;
}) {
  return (
    <li className="border-b border-secondary px-5 py-4 last:border-b-0 hover:bg-secondary">
      <div className="flex flex-wrap items-center gap-2">
        {title}
        {badges}
      </div>
      {children}
      <p className="mt-1.5 text-xs text-tertiary">{meta}</p>
    </li>
  );
}

function DocumentRow({ hit, spaceName }: { hit: DocumentHit } & RowProps) {
  return (
    <HitShell
      title={
        <Link
          {...documentLinkProps(hit.nodeId)}
          className="font-medium text-primary no-underline hover:underline"
        >
          {hit.title}
        </Link>
      }
      badges={
        <>
          {hit.document.type ? <DocumentTypeBadge type={hit.document.type} /> : null}
          <DocumentStatusBadge status={hit.document.status} />
          <ReviewBadge reviewBy={hit.document.reviewBy} status={hit.document.status} />
          {hit.document.hasDraft ? <WorkingDraftBadge /> : null}
        </>
      }
      meta={
        <>
          {spaceName ? `${spaceName} · ` : ''}
          updated {formatRelative(hit.updatedAt)}
        </>
      }
    >
      {/*
       * Plain text, in a text node. `ts_headline` is configured with empty
       * `StartSel`/`StopSel` (`search/config.ts`) precisely so that this line —
       * which shows body text from every document the viewer can reach — never
       * needs `dangerouslySetInnerHTML`. Do not add highlighting here by
       * re-introducing markup on the server.
       */}
      {hit.snippet ? <p className="mt-1.5 text-sm text-secondary">{hit.snippet}</p> : null}
    </HitShell>
  );
}

function FileRow({ hit, spaceName }: { hit: FileHit } & RowProps) {
  return (
    <HitShell
      title={
        <Link
          {...nodeLinkProps(hit.nodeId)}
          className="font-medium text-primary no-underline hover:underline"
        >
          {hit.title}
        </Link>
      }
      badges={<NodeTypeBadge type="file" />}
      meta={
        <>
          {spaceName ? `${spaceName} · ` : ''}
          updated {formatRelative(hit.updatedAt)}
        </>
      }
    >
      {hit.file ? (
        <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-secondary">
          <Mono className="text-secondary">{hit.file.filename}</Mono>
          <span className="text-tertiary">
            {contentTypeLabel(hit.file.contentType)} · {formatBytes(hit.file.sizeBytes)} · uploaded
            by {hit.file.uploadedByName || 'someone since removed'}
          </span>
        </p>
      ) : (
        // The API returns `file: null` rather than an object of nulls so this
        // state is renderable at all: the node exists and is findable by title,
        // and the bytes have not landed (§4.7).
        <p className="mt-1.5 text-sm text-tertiary">
          No bytes uploaded yet — this file node is findable by its title alone.
        </p>
      )}
    </HitShell>
  );
}

function LinkRow({ hit, spaceName }: { hit: LinkHit } & RowProps) {
  return (
    <HitShell
      title={
        <Link
          {...nodeLinkProps(hit.nodeId)}
          className="font-medium text-primary no-underline hover:underline"
        >
          {hit.title}
        </Link>
      }
      badges={
        <>
          <NodeTypeBadge type="link" />
          <ExternalLinkMarker url={hit.link?.url} />
        </>
      }
      meta={
        <>
          {spaceName ? `${spaceName} · ` : ''}
          updated {formatRelative(hit.updatedAt)}
        </>
      }
    >
      {hit.link ? (
        /*
         * The href is safe to render because the column is constrained to it:
         * `link_url_scheme` in the schema is `url ~ '^https?://'`, and the write
         * path refuses anything else besides. Nothing here parses the URL — §4.6
         * puts hostname extraction on the server so every surface says "opens
         * figma.com" the same way, and search's hit shape is the one payload
         * that does not carry it yet.
         */
        <a
          href={hit.link.url}
          target="_blank"
          rel="noreferrer noopener"
          className="mt-1.5 block truncate text-sm link"
          title={hit.link.url}
        >
          {hit.link.url}
        </a>
      ) : (
        <p className="mt-1.5 text-sm text-tertiary">
          No URL set yet — this link node is findable by its title alone.
        </p>
      )}
    </HitShell>
  );
}

export function SearchHitRow({ hit, spaceNames }: { hit: SearchHit; spaceNames: SpaceNames }) {
  const spaceName = spaceNames.get(hit.spaceId);

  switch (hit.kind) {
    case 'document':
      return <DocumentRow hit={hit} spaceName={spaceName} />;
    case 'file':
      return <FileRow hit={hit} spaceName={spaceName} />;
    case 'link':
      return <LinkRow hit={hit} spaceName={spaceName} />;
    default:
      // `service.ts` throws on a node of unknown type rather than skipping it,
      // and for the same reason: a row silently not drawn makes the page
      // shorter than the count beside it, which is the §9.2 symptom. Here the
      // union is closed, so this is a compile-time assertion first and a
      // runtime one only if the wire disagrees with `api/types.ts`.
      throw new Error(`search returned a hit of unknown kind: ${String((hit as SearchHit).kind)}`);
  }
}
